// Catalog.ID Client — Public Claim Detail Pane
// ============================================================================
// Shows details of a public claim (value, status, rejection reason) with delete action.

import { createEntityPaneType } from '@catalog-shell';
import type { EntityPaneConfig, ViewFieldDef, EntityAction } from '@catalog-shell';
import { claimTypeLabel } from './claims-list-pane';
import * as publicClaimsApi from '../api/public-claims';
import type { PublicClaimSummary } from '../api/types';
import { PANE_IDS } from '../shell-config';

const viewFields: ViewFieldDef<PublicClaimSummary>[] = [
    { key: 'claimType', label: 'Type', type: 'text', format: (v) => claimTypeLabel(v as string) },
    {
        key: 'publicValue', label: 'Published Value', type: 'text',
        format: (v) => (v as string) || '(pending review)',
    },
    {
        key: 'status', label: 'Status', type: 'badge',
        colorMap: {
            pending: 'rgba(100,149,237,0.3)',
            published: 'rgba(80,200,80,0.3)',
            rejected: 'rgba(220,50,50,0.3)',
        },
        labelMap: {
            pending: 'Pending',
            published: 'Published',
            rejected: 'Rejected',
        },
    },
    {
        key: 'rejectionReason', label: 'Rejection Reason', type: 'text',
        format: (v) => (v as string) || '—',
    },
    {
        key: 'publishedAt', label: 'Published', type: 'date', format: 'datetime',
    },
    {
        key: 'creationTime', label: 'Requested', type: 'date', format: 'datetime',
    },
];

const actions: EntityAction<PublicClaimSummary>[] = [
    {
        key: 'delete-public',
        label: 'Delete',
        modes: ['view'],
        variant: 'danger',
        execute: async (_model, ctx) => {
            const claim = ctx.instance.context['publicClaim'] as PublicClaimSummary;
            const ok = await ctx.shell.confirm({
                title: 'Delete Public Claim',
                message: 'This will remove the public claim. The underlying encrypted claim is not affected.',
                confirmLabel: 'Delete',
                confirmVariant: 'no',
            });
            if (!ok) return;
            try {
                await publicClaimsApi.deletePublicClaim(claim.publicClaimId);
                ctx.shell.toast('Public claim deleted', 'success');
                const surface = ctx.shell.getSurfaceForPane(ctx.instance.instanceId);
                surface?.closePane(ctx.instance.instanceId);
            } catch (err) {
                ctx.shell.toast(err instanceof Error ? err.message : 'Delete failed', 'error');
            }
        },
    },
];

const config: EntityPaneConfig<PublicClaimSummary> = {
    typeId: PANE_IDS.publicClaimDetail,
    defaultTitle: 'Public Claim',
    ariaLabel: 'Public claim details',
    refreshOnDataChanged: 'publicclaims',
    // Verbs live in the pane's own title row (shell USAGE.md "Where a button
    // belongs"): a single Delete, inline.
    actionsPlacement: 'pane',

    getModel: async (ctx) => {
        return ctx.instance.context['publicClaim'] as PublicClaimSummary;
    },

    view: {
        fields: viewFields,
        layout: [
            ['claimType', 'status'],
            ['publicValue'],
            ['rejectionReason'],
            ['publishedAt', 'creationTime'],
        ],
    },

    canEdit: false,
    canDelete: false,
    actions,
};

export function createPublicClaimDetailPane() {
    return createEntityPaneType(config);
}
